"use client";

import {
  MdArrowDownward,
  MdArrowUpward,
  MdKeyboardArrowDown,
  MdOutlineStar,
  MdSchedule,
} from "react-icons/md";
import styles from "./sorting-pill.module.css";
import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { usePageStore } from "../usePageStore";
import { SortingEnum } from "@/types/types";
import { useListingsCacheStore } from "@/global-cache/useListingsCacheStore";

export default function SortingPillClient({ locale }: { locale: string }) {
  const [isOpen, setIsOpen] = useState(false);
  const sortingPillRef = useRef<HTMLDivElement>(null);
  const t = useTranslations("SearchResult.Sorting");
  const currentSortingValue = usePageStore((state) => state.search.sorting);
  const setSortingValue = usePageStore((state) => state.setSortingValue);
  const setCache = useListingsCacheStore((state) => state.setMultipleListings);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sortingPillRef.current &&
        !sortingPillRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);
  return (
    <div className={styles.sortingWrapper} ref={sortingPillRef}>
      <span className={styles.sortingTitle}>{t("title")}</span>
      <button
        className={styles.sortingPill}
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
      >
        <span className={styles.activeText}>{t(currentSortingValue)}</span>
        <MdKeyboardArrowDown />
      </button>
      {isOpen && (
        <ul className={styles.selectorWrapper}>
          <li>
            <button
              className={`${
                currentSortingValue === SortingEnum.TOPLISTINGS &&
                styles.isActive
              }`}
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsOpen(false);
                await setSortingValue(
                  SortingEnum.TOPLISTINGS,
                  locale,
                  setCache
                );
              }}
            >
              <span className={styles.icon}>
                <MdOutlineStar />
              </span>
              <span className={styles.text}>{t("top-listings")}</span>
            </button>
          </li>
          <li>
            <button
              className={`${
                currentSortingValue === SortingEnum.LOWESTPRICE &&
                styles.isActive
              }`}
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsOpen(false);
                await setSortingValue(
                  SortingEnum.LOWESTPRICE,
                  locale,
                  setCache
                );
              }}
            >
              <span className={styles.icon}>
                <MdArrowUpward />
              </span>
              <span className={styles.text}>{t("lowest-price")}</span>
            </button>
          </li>
          <li>
            <button
              className={`${
                currentSortingValue === SortingEnum.HIGHESTPRICE &&
                styles.isActive
              }`}
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsOpen(false);
                await setSortingValue(
                  SortingEnum.HIGHESTPRICE,
                  locale,
                  setCache
                );
              }}
            >
              <span className={styles.icon}>
                <MdArrowDownward />
              </span>
              <span className={styles.text}>{t("highest-price")}</span>
            </button>
          </li>
          <li>
            <button
              className={`${
                currentSortingValue === SortingEnum.NEWESTLISTINGS &&
                styles.isActive
              }`}
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                setIsOpen(false);
                await setSortingValue(
                  SortingEnum.NEWESTLISTINGS,
                  locale,
                  setCache
                );
              }}
            >
              <span className={styles.icon}>
                <MdSchedule />
              </span>
              <span className={styles.text}>{t("newest-listings")}</span>
            </button>
          </li>
        </ul>
      )}
    </div>
  );
}

export function SortingPillLoading() {
  const t = useTranslations("SearchResult.Sorting");
  return (
    <div className={styles.sortingWrapper}>
      <span className={styles.sortingTitle}>{t("title")}</span>
      <div className={styles.sortingPill}>
        <span className={styles.activeText}>{t("top-listings")}</span>
        <MdKeyboardArrowDown />
      </div>
    </div>
  );
}
